import { useMutation } from '@apollo/client';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import Gallery from '@website/components/directoryTheme/Gallery';
import Stars from '@website/components/directoryTheme/Stars';
import { useDeleteReview } from '@website/components/hosts/dashboard/reviews/admin/DeleteReview';
import { useRemoveReplyFromReview } from '@website/components/hosts/dashboard/reviews/admin/RemoveReplyFromReview';
import { ReplyToReviewForm } from '@website/components/hosts/dashboard/reviews/admin/ReplyToReview';
import ConfirmDialog from '@website/components/utilities/ConfirmDialog';
import Truncate from '@website/components/utilities/Truncate';
import {
  Currency,
  DeleteMediaDocument,
  Maybe,
  ReviewSource,
  Status,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import Link from 'next/link';
import React, { useState } from 'react';
import { Col, Dropdown, Row } from 'react-bootstrap';
import { BsReply } from 'react-icons/bs';
import { FaEllipsisV } from 'react-icons/fa';

export type ReviewsAlgoliaHit = {
  objectID: string;
  id: string;
  body: string;
  rating: number;
  startDate: string;
  updated: string;
  source: Maybe<ReviewSource>;
  fiveStarRating: number;
  reply: Maybe<{ content: string; created: string }>;
  media: Maybe<{
    items: Array<{
      id: string;
      file: Maybe<{ key: string; bucket: string; region: string }>;
    }>;
  }>;
  hostID: string;
  userID?: string;
  groupID?: string;
  tripID: string;
  user?: {
    givenName: string;
    id: string;
  };
  seededReviewsMedia?: {
    id: string;
  }[];
  guest: {
    id: string;
    user: Maybe<{
      id: string;
      givenName: string;
      familyName: string;
      email: string;
    }>;
    booking: {
      id: string;
      group: {
        id: string;
        start: { utc: string; zone: string };
        guestPrice: { amountZD: number; currency: Currency };
        trip: {
          id: string;
          active: Status;
          description: string;
          title: string;
          urlSlug: string;
          host: { id: string; name: string };
        };
      };
    };
  };
};

type CustomToggleProps = {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
};

// eslint-disable-next-line react/display-name
export const CustomToggle: React.FC<CustomToggleProps> = React.forwardRef(
  ({ children, onClick }, ref) => (
    <a
      href=""
      // @ts-expect-error
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      {children}
    </a>
  ),
);

export function ReviewsActionCell({
  row,
}: CellContext<ReviewsAlgoliaHit, any>) {
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [showReplyPrompt, setShowReplyPrompt] = useState(false);
  const [showRemoveReplyPrompt, setShowRemoveReplyPrompt] = useState(false);
  const { deleteReview, loading: isDeleting } = useDeleteReview();
  const { removeReplyMutation, loading: isRemoveReplyLoading } =
    useRemoveReplyFromReview();

  return (
    <>
      <ConfirmDialog
        body={
          <Truncate lines={3} enableExpand className="text-muted text-sm">
            {row.original.body}
          </Truncate>
        }
        title={`Are you sure you want to delete this review by ${row.original.user?.givenName}?`}
        visible={showDeletePrompt}
        setVisible={setShowDeletePrompt}
        onConfirmFn={() => deleteReview(row.original.id)}
      />

      <ConfirmDialog
        body={
          <Truncate lines={3} enableExpand className="text-muted text-sm">
            {row.original.reply?.content}
          </Truncate>
        }
        title={`Are you sure you want to remove the reply from this review by ${row.original?.user?.givenName}?`}
        visible={showRemoveReplyPrompt}
        setVisible={setShowRemoveReplyPrompt}
        onConfirmFn={() =>
          removeReplyMutation({
            variables: {
              id: row.original.id,
            },
          })
        }
      />

      <ReplyToReviewForm
        review={{
          id: row.original.id,
          // reply: row.original,
          user: row.original.user,
          reply: row.original.reply,
        }}
        open={showReplyPrompt}
        onClose={() => setShowReplyPrompt(false)}
      />

      <Dropdown>
        <Dropdown.Toggle as={CustomToggle} variant="outline-primary">
          <FaEllipsisV />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Link
            href={`/me/${row.original?.userID}/reviews/${row.original.id}`}
            passHref
            legacyBehavior
          >
            <Dropdown.Item href="#">Edit Review</Dropdown.Item>
          </Link>
          <Dropdown.Item onClick={() => setShowDeletePrompt(true)}>
            {isDeleting ? 'Deleting...' : 'Delete Review'}
          </Dropdown.Item>
          <Dropdown.Item onClick={() => setShowReplyPrompt(true)}>
            Reply to Review
          </Dropdown.Item>
          {row.original.reply ? (
            <Dropdown.Item onClick={() => setShowRemoveReplyPrompt(true)}>
              {isRemoveReplyLoading
                ? 'Removing reply...'
                : 'Remove Reply from Review'}
            </Dropdown.Item>
          ) : null}
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
}

export const adminReviewsColumns: ColumnDef<ReviewsAlgoliaHit, any>[] = [
  {
    header: 'Created',
    size: 100,
    cell: ({ row }) => {
      return DateTime.fromISO(row.original.updated).toLocaleString(
        DateTime.DATETIME_SHORT,
      );
    },
  },
  {
    header: 'User',
    size: 100,
    cell: ({ row }) => {
      const name = `${row.original.guest.user?.givenName ?? ''} ${
        row.original.guest.user?.familyName ?? ''
      }`;

      return (
        <Link
          className="text-break text-wrap"
          href={`/me/${row.original.guest.user?.id}`}
        >
          {name}
        </Link>
      );
    },
  },
  {
    header: 'Review',
    size: 350,
    cell: ({ row }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [imageToDelete, setImageToDelete] = useState<string | null>(null);

      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [deleteMedia] = useMutation(DeleteMediaDocument, {
        onCompleted: () => {
          alert('Image deleted');
        },
      });

      return (
        <>
          <Row>
            <Col xs={12}>
              <Truncate
                lines={3}
                enableExpand
                enableCollapse
                className="text-muted text-sm"
              >
                {row.original.body}
              </Truncate>
            </Col>
          </Row>
          {row.original.reply && (
            <div className="d-flex flex-row">
              <div className="text-center p-2">
                <BsReply
                  style={{
                    transform: 'scaleX(-1) scaleY(-1)',
                  }}
                  size={18}
                />
              </div>
              <div
                style={{
                  backgroundColor: '#EFEFEF',
                }}
                className="flex-1 p-1 p-2 rounded"
              >
                <Truncate
                  lines={3}
                  enableExpand
                  enableCollapse
                  className="text-sm text-muted"
                >
                  {row.original.reply.content}
                </Truncate>
              </div>
            </div>
          )}
          {row.original.media?.items?.length > 0 && (
            <Row>
              <Col className="py-3" xs={12}>
                <Gallery
                  images={row.original.media.items.slice(0, 6)}
                  allImages={row.original.media.items}
                  xs={6}
                  sm={3}
                  singleRowOnly
                  onDelete={(id) => setImageToDelete(id)}
                />
              </Col>
            </Row>
          )}

          <ConfirmDialog
            body="Are you sure you want to delete this image?"
            title="Delete Image"
            visible={!!imageToDelete}
            setVisible={(visible) => {
              if (!visible) {
                setImageToDelete(null);
              }
            }}
            onConfirmFn={async () => {
              await deleteMedia({
                variables: {
                  id: imageToDelete,
                },
              });
              setImageToDelete(null);
            }}
          />
        </>
      );
    },
  },
  {
    header: 'Trip',
    size: 200,
    cell: ({ row }) => {
      return (
        <Link
          href={{
            pathname: `/trips/${row.original.guest.booking.group.trip.urlSlug}`,
          }}
        >
          {row.original.guest.booking.group.trip.title}
        </Link>
      );
    },
  },
  {
    header: 'Start Date',
    size: 150,
    cell: ({ row }) => {
      return DateTime.fromISO(row.original.startDate).toLocaleString(
        DateTime.DATETIME_MED,
      );
    },
  },
  {
    header: 'Booking ID',
    size: 50,
    cell: ({ row }) => {
      return (
        <Link href={`/me/${row.original.guest.booking.id}`}>
          {row.original.guest.booking.id}
        </Link>
      );
    },
  },
  {
    header: 'Rating',
    size: 80,
    cell: ({ row }) => {
      const { rating } = row.original;

      // convert rating from 0-1 to 0-5
      const fiveStarRating = Math.round(rating * 5);

      return <Stars size="sm" color="text-primary" stars={fiveStarRating} />;
    },
  },
  {
    header: '',
    id: 'actions',
    size: 30,
    cell: ReviewsActionCell,
  },
];
